// Simplified NB55 printer helper — Feb 2026 rewrite.
//
// All vendor JS bridges (TalabyaPrinter, NB55Printer, SunmiPrinter, …)
// and all AIDL probes were deleted. The ONLY supported path is the
// companion standalone APK ("جسر طابعة طلبية") that listens on
// http://127.0.0.1:8899 and talks ESC/POS over Bluetooth RFCOMM to the
// MAC address the operator entered inside the APK.

const BRIDGE_URL = "http://127.0.0.1:8899";
const HEALTH_CACHE_KEY = "talabya:bt-bridge-ok";
const HEALTH_TTL_MS = 30_000;

let _healthOk = null;
let _healthAt = 0;

async function _probeBridge() {
  const now = Date.now();
  if (_healthOk !== null && (now - _healthAt) < HEALTH_TTL_MS) return _healthOk;
  try {
    const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const to = ctl ? setTimeout(() => ctl.abort(), 2500) : null;
    const r = await fetch(`${BRIDGE_URL}/health`, { signal: ctl?.signal });
    if (to) clearTimeout(to);
    _healthOk = r.ok;
  } catch (e) {
    // Surface the exact failure in DevTools so the operator can tell apart
    // "APK not running" from "Chrome Private Network Access blocked" from
    // "CORS misconfig".
    try { console.warn("[talabya] bridge probe failed:", e?.message || e); } catch { /* noop */ }
    _healthOk = false;
  }
  _healthAt = now;
  try { localStorage.setItem(HEALTH_CACHE_KEY, _healthOk ? "1" : "0"); } catch { /* noop */ }
  return _healthOk;
}

// Kick off a probe on module load so the sync check has a cached answer.
if (typeof window !== "undefined") _probeBridge();

/** Returns true if the companion APK bridge is reachable on localhost. */
export function isNb55BridgeAvailable() {
  if (typeof window === "undefined") return false;
  try { if (localStorage.getItem(HEALTH_CACHE_KEY) === "1") return true; } catch { /* noop */ }
  return false;
}

/** Fire-and-forget async print via the localhost bridge. */
async function _bridgePrint(text) {
  try {
    const r = await fetch(`${BRIDGE_URL}/print`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    });
    return r.ok;
  } catch { return false; }
}

/** Print plain text via the companion APK bridge. Returns true on success,
 *  false if the bridge isn't reachable (caller then falls back to iframe). */
export function nb55PrintText(text) {
  if (typeof window === "undefined") return false;
  try {
    if (localStorage.getItem(HEALTH_CACHE_KEY) === "1") {
      _bridgePrint(text).then((ok) => { if (!ok) _probeBridge(); });
      return true;
    }
  } catch { /* noop */ }
  return false;
}

/** Returns "http" when the bridge APK is reachable, else "none". */
export function nb55BridgeKind() {
  if (typeof window === "undefined") return "none";
  try { if (localStorage.getItem(HEALTH_CACHE_KEY) === "1") return "http"; } catch { /* noop */ }
  return "none";
}

/** Manually re-probe the localhost bridge (called from PrinterSetup). */
export async function refreshNyxBridge() {
  _healthOk = null;
  _healthAt = 0;
  return await _probeBridge();
}

/** Fetch the current state of the APK bridge (connected? which MAC?). */
export async function getBridgeStatus() {
  try {
    const r = await fetch(`${BRIDGE_URL}/health`);
    if (!r.ok) return { ok: false, error: `HTTP ${r.status}` };
    const j = await r.json();
    return { ok: true, ...j };
  } catch (e) {
    const msg = String(e?.message || e);
    try { console.warn("[talabya] getBridgeStatus failed:", msg); } catch { /* noop */ }
    return { ok: false, error: msg };
  }
}

/** Convert an order to a plain-text 58mm/80mm receipt. */
export function orderToText(order) {
  const items = Array.isArray(order?.items) ? order.items : [];
  const lines = [];
  const sep = "-".repeat(32);
  const dsep = "=".repeat(32);
  const fmt = (label, price, width = 32) => {
    const p = Number(price || 0).toFixed(2);
    const pad = Math.max(1, width - label.length - p.length);
    return label + " ".repeat(pad) + p;
  };
  lines.push(dsep);
  lines.push("       طلبية  Talabya");
  lines.push("       ==============");
  lines.push(dsep);
  if (order.restaurant_name) lines.push("محل  : " + order.restaurant_name);
  if (order.code) lines.push("طلب #: " + order.code);
  if (order.daily_seq != null) lines.push("رقم اليوم: " + order.daily_seq);
  lines.push(sep);
  lines.push(fmt("الصنف", "السعر"));
  lines.push(sep);
  for (const it of items) {
    const qty = it.qty || it.quantity || 1;
    const name = (it.name || "").slice(0, 20);
    const unit = Number(it.unit_price != null ? it.unit_price : it.price || 0);
    const base = unit * qty;
    lines.push(fmt(`${qty}x ${name}`, base));
    const extras = Array.isArray(it.extras) ? it.extras : [];
    for (const x of extras) {
      if (!x?.name) continue;
      const xn = String(x.name).slice(0, 18);
      const xp = Number(x.price || 0) * qty;
      lines.push(fmt(`   + ${xn}`, xp));
    }
    if (it.note || it.notes) lines.push("   * " + (it.note || it.notes));
  }
  lines.push(sep);
  if (order.subtotal != null) lines.push(fmt("المجموع الفرعي:", order.subtotal));
  if (order.delivery_fee) lines.push(fmt("رسوم التوصيل:", order.delivery_fee));
  lines.push(dsep);
  lines.push(fmt("الإجمالي (د.ل):", order.total || 0));
  lines.push(dsep);
  if (order.customer_name) lines.push("زبون: " + order.customer_name);
  if (order.customer_phone) lines.push("هاتف: " + order.customer_phone);
  if (order.delivery_address) lines.push("عنوان: " + order.delivery_address);
  lines.push(sep);
  lines.push("     شكراً لاختياركم طلبية");
  lines.push("");
  lines.push("");
  lines.push("");
  return lines.join("\n");
}
